import {
  useCalculateHeight,
  useElementInView,
  useElementTopOfView,
  useGlobalState,
  usePreventBodyScroll,
} from '@/hooks';
import type { IHeader } from '@/interfaces';
import { gtmClickNavigationOpen } from '@/utils';
import { useRouter } from 'next/router';
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import AccordionMenu from './AccordionMenu';
import {
  CollapseContainer,
  CollapseNavigationWrapper,
  Divider,
  HamburgerButton,
  HamburgerButtonLine,
} from './CollapseNavigation.styles';

import Languages from '../Languages';
import PMCSection from './PMCSection';

const CollapseNavigation: React.FC<IHeader> = (props) => {
  const { itemList, mainText, subText, button } = props;
  const router = useRouter();
  const { isMultiLanguagePage } = useGlobalState();
  const [isExpand, setIsExpand] = useState<boolean>(false);
  const collapseWrapperRef = React.useRef<HTMLDivElement>(null);
  const isHeaderTopOfView = useElementTopOfView('header');
  const isAdTopOfView = useElementTopOfView('ads-top-of-page');
  const isAdInView = useElementInView('ads-top-of-page');
  const topEleHeights = useCalculateHeight(isAdInView && isAdTopOfView ? ['header', 'ads-top-of-page'] : ['header'], [
    isAdTopOfView,
    isAdInView,
  ]);
  usePreventBodyScroll(isExpand);

  useEffect(() => {
    !!isExpand && setIsExpand(false);
  }, [router]);

  // Focus management - return focus to button when menu closes
  useEffect(() => {
    if (!isExpand && collapseWrapperRef.current) {
      collapseWrapperRef.current.focus();
    }
  }, [isExpand]);

  const CollapseMenuElement = () => {
    let Collapse = <AccordionMenu data={itemList} isCollapseAll={!isExpand} />;

    if (isMultiLanguagePage) {
      Collapse = (
        <>
          <Languages />
          <Divider />
          <PMCSection mainText={mainText} subText={subText} buttonText={button} />
        </>
      );
    }

    return <CollapseContainer topEleHeights={topEleHeights}>{Collapse}</CollapseContainer>;
  };

  const createCollapseMenuPortal = () => {
    return createPortal(CollapseMenuElement(), document.getElementById('sticky-collapse-menu') as Element);
  };

  const renderCollapseMenu = () => {
    if (!isHeaderTopOfView) {
      return CollapseMenuElement();
    }
    return createCollapseMenuPortal();
  };

  const handleButtonClick = () => {
    if (!isExpand && !isMultiLanguagePage) {
      gtmClickNavigationOpen();
    }
    setIsExpand(!isExpand);
  };

  return (
    <CollapseNavigationWrapper ref={collapseWrapperRef} isMultiLanguagePage={isMultiLanguagePage}>
      <HamburgerButton
        role='button'
        onClick={handleButtonClick}
        aria-expanded={isExpand}
        aria-label={`${isExpand ? 'Close' : 'Open'} hamburger menu`}
        tabIndex={0}
      >
        <HamburgerButtonLine />
        <HamburgerButtonLine />
        <HamburgerButtonLine />
      </HamburgerButton>
      {isExpand && renderCollapseMenu()}
    </CollapseNavigationWrapper>
  );
};

export default CollapseNavigation;
